import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { BookOpenCheck, LogIn } from "lucide-react";

const LOGIN_BG =
    "https://static.prod-images.emergentagent.com/jobs/69127f46-487f-4c9b-81b6-5c40b97c2506/images/c7c0aa23d5a6c5859b39eefc802f0e7f50dad64278ae75e1ff1d40d19f4cca63.png";

export default function Login() {
    const { loginWithEmail } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await loginWithEmail(email, password);
            toast.success("Willkommen zurück");
            navigate("/verlag-waehlen", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Anmeldung fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left – brand panel */}
            <div
                className="hidden lg:flex w-1/2 relative items-end p-12 bg-cover bg-center"
                style={{
                    backgroundImage: `linear-gradient(135deg, rgba(26,26,46,0.85), rgba(26,26,46,0.55)), url(${LOGIN_BG})`,
                }}
            >
                <div className="absolute inset-0 grid-bg opacity-30" />
                <div className="relative z-10 max-w-md">
                    <div className="flex items-center gap-3 mb-8 text-foreground">
                        <BookOpenCheck className="h-7 w-7" />
                        <span className="font-heading text-xl tracking-tight">INVENTUR PRO</span>
                    </div>
                    <h1 className="font-heading text-5xl tracking-tighter font-bold text-foreground leading-[1.05]">
                        Lagerbewertung.
                        <br />
                        <span className="text-tenant">Präzise.</span> Mandantenfähig.
                    </h1>
                    <p className="mt-6 text-muted-foreground leading-relaxed max-w-sm">
                        Verwalten Sie die Inventurbewertung von Buchbeständen über drei Verlage hinweg – mit
                        Wertberichtigung, Bilanzwert und Excel-Import in einer einzigen Konsole.
                    </p>
                </div>
            </div>

            {/* Right – auth panel */}
            <div className="flex-1 flex items-center justify-center p-6 bg-background relative">
                <div className="grid-bg absolute inset-0 opacity-[0.06]" />
                <Card className="relative w-full max-w-md p-8 border-border bg-surface1">
                    <div className="flex items-center gap-2 mb-6 lg:hidden">
                        <BookOpenCheck className="h-6 w-6 text-tenant" />
                        <span className="font-heading text-lg tracking-tight">INVENTUR PRO</span>
                    </div>
                    <h2 className="font-heading text-2xl tracking-tight font-semibold">Anmeldung</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Bitte melden Sie sich mit E-Mail und Passwort an.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                        Benutzerkonten werden ausschließlich durch Administratoren angelegt.
                    </p>

                    <form onSubmit={handleLogin} className="space-y-4 mt-6">
                        <div className="space-y-1.5">
                            <Label htmlFor="email">E-Mail</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@verlag.at"
                                required
                                data-testid="login-email-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password">Passwort</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="login-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={busy}
                            className="w-full bg-tenant hover:opacity-90 text-[color:var(--tenant-accent-foreground)]"
                            data-testid="login-submit-button"
                        >
                            <LogIn className="h-4 w-4 mr-2" />
                            Anmelden
                        </Button>
                    </form>
                </Card>
            </div>
        </div>
    );
}
